import { AlertTriangle, Loader2, Timer } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import type { WricefUsageRulePreview } from "@/api/wricef-decisions";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useCurrentUser } from "@/hooks/useAuth";
import { useApplyWricefUsageRule, useWricefUsageRule } from "@/hooks/useWricefUsageRule";
import { detailCode } from "@/lib/api-error";
import { formatDateOnly } from "@/lib/datetime";
import { tp } from "@/lib/plural";
import { hasMinRole } from "@/lib/roles";

/** Quantos nomes a prévia mostra antes de resumir o resto. */
const PREVIEW_NAMES = 12;

/**
 * A regra de "Fora de escopo por falta de uso" no Catálogo WRICEF (#1622).
 *
 * O consultor da Exed faz a primeira avaliação de centenas de objetos, e a regra
 * faz o primeiro corte. A lista vai ao cliente pelo relatório da EF-ARCH-002, que
 * lê a decisão e a justificativa de cada objeto, e o que ele disser que usa volta.
 * Por isso a prévia diz, antes de aplicar, a fonte, a janela e, abaixo de um ano,
 * que a marcação precisa de revisão: é o mesmo texto que vai para cada objeto.
 */
export function WricefUsageRuleButton({ projectId }: { projectId: string }) {
  const { t } = useTranslation();
  const canDecide = hasMinRole(useCurrentUser()?.role, "consultant");
  const [open, setOpen] = useState(false);

  if (!canDecide) return null;

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        className="h-8"
        onClick={() => setOpen(true)}
        title={t("Mark as out of scope the deliverables the usage source saw no execution of")}
      >
        <Timer className="h-3.5 w-3.5 mr-1" />
        {t("Mark out of scope by usage")}
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl">
          {/* As consultas vivem aqui dentro, montadas só com a janela aberta: a
              prévia recalcula o plano no servidor, e a barra da lista renderiza
              sem React Query nos testes da aba. */}
          <UsageRuleDialogBody projectId={projectId} onClose={() => setOpen(false)} />
        </DialogContent>
      </Dialog>
    </>
  );
}

function UsageRuleDialogBody({ projectId, onClose }: { projectId: string; onClose: () => void }) {
  const { t } = useTranslation();
  const preview = useWricefUsageRule(projectId, true);
  const apply = useApplyWricefUsageRule(projectId);
  const candidates = preview.data?.candidates?.length ?? 0;

  return (
    <>
      <DialogHeader>
        <DialogTitle>{t("Mark out of scope by usage")}</DialogTitle>
        <DialogDescription>
          {t("Objects that already have a decision are never changed.")}
        </DialogDescription>
      </DialogHeader>
      {preview.isLoading ? (
        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
      ) : preview.data ? (
        <UsageRulePreview preview={preview.data} applied={apply.data?.updated} error={apply.error} />
      ) : (
        <p role="alert" className="text-sm text-destructive">
          {t("Could not load the usage rule preview.")}
        </p>
      )}
      <DialogFooter>
        <Button variant="outline" size="sm" onClick={onClose}>
          {t("Close")}
        </Button>
        {preview.data?.available && candidates > 0 && apply.data == null && (
          <Button
            size="sm"
            disabled={apply.isPending}
            onClick={() => apply.mutate(preview.data?.window?.execution_id)}
          >
            {apply.isPending && <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />}
            {t("Mark {{n}} out of scope", { n: candidates })}
          </Button>
        )}
      </DialogFooter>
    </>
  );
}

/** Os motivos com que o servidor recusa a regra, na língua de quem lê. */
const UNAVAILABLE_REASON: Record<string, string> = {
  no_functional_execution: "The project has no completed Functional execution.",
  usage_not_measured: "The Functional execution did not measure transaction usage.",
  window_unknown: "The usage source did not declare how many days it covered.",
  no_execution_attributed:
    "The usage source saw no object running, so it cannot tell idle from unmeasured.",
  usage_evidence_changed:
    "The Functional measurement changed after the preview. Open the preview again.",
};

function UsageRulePreview({
  preview,
  applied,
  error,
}: {
  preview: WricefUsageRulePreview;
  applied: number | undefined;
  error: unknown;
}) {
  const { t } = useTranslation();
  const candidates = preview.candidates ?? [];
  const counts = preview.counts ?? {};
  const window = preview.window;
  const errorCode = error ? detailCode(error) : undefined;

  return (
    <div data-testid="wricef-usage-rule-preview" className="space-y-3 text-sm">
      {!preview.available || !window ? (
        <p className="text-muted-foreground">
          {t(
            UNAVAILABLE_REASON[preview.unavailable_reason ?? ""] ??
              "The usage rule cannot be applied to this project.",
          )}
        </p>
      ) : (
        <>
          <p>
            {t("Maximum window observable by {{source}}: {{days}} days, up to {{date}}.", {
              source: window.source_label,
              days: window.covered_days ?? 0,
              date: formatDateOnly(window.measured_on),
            })}
          </p>
          {window.needs_review && (
            <p className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-amber-900">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>{t("The window is shorter than 1 year, so the marking needs review.")}</span>
            </p>
          )}
          {candidates.length === 0 ? (
            <p className="text-muted-foreground">{t("No deliverable to mark.")}</p>
          ) : (
            <p className="font-medium">
              {tp(
                t,
                candidates.length,
                "{{n}} deliverable without execution in the window will be marked out of scope.",
                "{{n}} deliverables without execution in the window will be marked out of scope.",
                { n: candidates.length },
              )}
            </p>
          )}
          <p className="text-muted-foreground">
            {t(
              "Ran in the window: {{executed}} · Created in the last 12 months: {{recent}} · Already decided: {{decided}} · Not measurable by the source: {{unmeasured}}",
              {
                executed: counts.executed ?? 0,
                recent: counts.recent ?? 0,
                decided: counts.already_decided ?? 0,
                unmeasured: counts.not_measurable ?? 0,
              },
            )}
          </p>
          {candidates.length > 0 && (
            <p className="font-mono text-xs text-muted-foreground">
              {candidates
                .slice(0, PREVIEW_NAMES)
                .map((c) => c.obj_name)
                .join(", ")}
              {candidates.length > PREVIEW_NAMES &&
                ` ${t("and {{n}} more", { n: candidates.length - PREVIEW_NAMES })}`}
            </p>
          )}
          {preview.note && (
            <div>
              <p className="text-xs font-medium">{t("Justification recorded on each object:")}</p>
              <p className="mt-1 rounded-md border bg-muted/40 px-3 py-2 text-xs">{preview.note}</p>
            </div>
          )}
        </>
      )}
      {applied != null && (
        <p role="status" className="font-medium text-emerald-700">
          {tp(t, applied, "{{n}} object marked out of scope.", "{{n}} objects marked out of scope.", {
            n: applied,
          })}
        </p>
      )}
      {error != null && (
        <p role="alert" className="text-destructive">
          {t(UNAVAILABLE_REASON[errorCode ?? ""] ?? "Could not apply the usage rule.")}
        </p>
      )}
    </div>
  );
}
